import { FC } from 'react';
import { Button, CircularProgress, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

import { Modal } from 'src/presentations/components/molecules/modal/Modal';
import { LegalEntityDto } from 'src/generated/services/TFinancialApi';
import { beautifyEnum } from 'src/utils/utility';

interface MergeConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  entityToDelete?: LegalEntityDto;
  entityToKeep?: LegalEntityDto;
  isLoading?: boolean;
}

const StyledModal = styled(Modal)({
  '& .MuiDialog-paper': {
    maxWidth: '600px',
    borderRadius: '16px',

    '& .MuiDialogContent-root': {
      padding: '0',
      backgroundColor: '#ffffff',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
  },
});

const EntityCard = ({ entity, label, isDelete = false }: { entity?: LegalEntityDto; label: string; isDelete?: boolean }) => {
  if (!entity) return null;

  return (
    <div className='p-16 bg-neutral-10 rounded-8'>
      <p className='text-10 text-neutral-50 mb-8'>{label}</p>
      <h3 className='text-16 font-bold mb-8'>{entity.name}</h3>
      <div className='flex items-center gap-10'>
        <span className={`text-14 font-medium ${isDelete ? 'text-red-40' : 'text-primary'}`}>{entity.id}</span>
        <div className='w-1 h-14 bg-[#D9D9D9]' />
        <span className={`text-14 font-medium ${isDelete ? 'text-red-40' : 'text-primary'}`}>{beautifyEnum(entity.code || '')}</span>
        {isDelete && <CloseIcon className='text-red-40 ml-auto' fontSize='small' />}
      </div>
    </div>
  );
};

const MergeConfirmationModal: FC<MergeConfirmationModalProps> = ({ open, onClose, onConfirm, entityToDelete, entityToKeep, isLoading = false }) => (
  <StyledModal open={open} maxWidth='md' fullWidth>
    <>
      <div className='p-24 pb-0'>
        <div className='flex items-center justify-between gap-16 mb-16'>
          <h2 className='text-24 font-bold'>Confirmation</h2>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <p className='text-16 text-neutral-70 mb-24'>Are you sure want to merge these followings entities:</p>

        <div className='flex flex-col gap-16'>
          <EntityCard entity={entityToDelete} label='Selected legal entity that will be deleted' isDelete />
          <EntityCard entity={entityToKeep} label='Legal entity that will be used instead going forward' />
        </div>
      </div>

      {/* Action buttons */}
      <div className='p-24 flex items-center justify-between gap-24'>
        <Button
          variant='outlined'
          color='primary'
          onClick={onClose}
          disabled={isLoading}
          sx={{
            width: '100%',
            borderColor: '#00A651',
            color: '#00A651',
            '&:hover': {
              backgroundColor: '#efefef',
            },
            '&.Mui-disabled': {
              borderColor: '#E5E7EB',
              color: '#9CA3AF',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={onConfirm}
          disabled={isLoading}
          sx={{
            backgroundColor: '#00A651',
            width: '100%',
            '&:hover': {
              backgroundColor: '#009048',
            },
            '&.Mui-disabled': {
              backgroundColor: '#E5E7EB',
              color: '#9CA3AF',
            },
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
              Processing...
            </>
          ) : (
            'Confirm'
          )}
        </Button>
      </div>
    </>
  </StyledModal>
);

export default MergeConfirmationModal;
